
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, X, Image } from "lucide-react";

interface ImagePreviewProps {
  urls: string[];
}

export const ImagePreview = ({ urls }: ImagePreviewProps) => {
  return (
    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
      {urls
        .filter(url => url.trim())
        .map((url, index) => (
          <div key={`preview-${index}`} className="relative aspect-square rounded-md overflow-hidden bg-muted">
            <img
              src={url}
              alt={`Aperçu ${index + 1}`}
              onError={(e) => {
                console.log("Image load error for URL:", url);
                (e.target as HTMLImageElement).src = '/placeholder.svg';
              }}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
    </div>
  );
};

interface ImageUploaderProps {
  imageUrls: string[];
  isSubmitting?: boolean;
  onImageUrlsChange: (urls: string[]) => void;
}

export const ImageUploader = ({ imageUrls, isSubmitting = false, onImageUrlsChange }: ImageUploaderProps) => {
  const [newImageUrl, setNewImageUrl] = useState("");
  
  const handleAddImageField = () => {
    if (newImageUrl.trim()) {
      onImageUrlsChange([...imageUrls, newImageUrl.trim()]);
      setNewImageUrl("");
    } else {
      onImageUrlsChange([...imageUrls, ""]);
    }
  };

  const handleRemoveImageField = (index: number) => {
    if (imageUrls.length > 1) {
      onImageUrlsChange(imageUrls.filter((_, i) => i !== index));
    } else {
      // Keep at least one field but clear it
      onImageUrlsChange([""]);
    }
  };

  const handleImageUrlChange = (index: number, value: string) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    onImageUrlsChange(newUrls);
  };

  const validateImageUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    // Basic URL validation
    return url.startsWith('http://') || url.startsWith('https://');
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="images">Images du produit (URLs)</Label>
      
      <div className="space-y-2">
        {imageUrls.map((url, index) => (
          <div key={index} className="flex gap-2 items-start">
            <Input
              value={url}
              onChange={(e) => handleImageUrlChange(index, e.target.value)}
              placeholder="https://example.com/image.jpg"
              disabled={isSubmitting}
              className={`flex-1 ${validateImageUrl(url) ? '' : 'border-red-300 focus-visible:ring-red-300'}`}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => handleRemoveImageField(index)}
              disabled={isSubmitting}
              className="flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={handleAddImageField}
          className="w-full"
          disabled={isSubmitting}
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une image
        </Button>
      </div>
      
      {imageUrls.some(url => url.trim()) && (
        <ImagePreview urls={imageUrls.filter(url => validateImageUrl(url))} />
      )}
      
      <Alert className="mt-2">
        <AlertDescription>
          Saisissez les URLs complètes des images pour votre produit (commençant par http:// ou https://). Vous pouvez en ajouter plusieurs.
        </AlertDescription>
      </Alert>
    </div>
  );
};
